import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../api/ApiProvider.js';
import { useActiveClassroom } from '../../context/ActiveClassroomProvider.js';
import { isoDate } from '../../lib/localDate.js';
import { webUrlFor, youtubeUrlFor } from '../../lib/searchUrls.js';
import type { SearchEngineDto } from '../../api/types.js';
import styles from './Search.module.css';

const ENGINES: {
  key: SearchEngineDto;
  label: string;
  placeholder: string;
  dotClass: string;
}[] = [
  { key: 'kiddle', label: 'Kiddle', placeholder: 'Search Kiddle…', dotClass: 'engineDotKiddle' },
  { key: 'google', label: 'Google', placeholder: 'Search Google…', dotClass: 'engineDotGoogle' },
  { key: 'bing', label: 'Bing', placeholder: 'Search Bing…', dotClass: 'engineDotBing' },
];

export function Search() {
  const api = useApi();
  const qc = useQueryClient();
  const { active } = useActiveClassroom();
  const [webQuery, setWebQuery] = useState('');
  const [ytQuery, setYtQuery] = useState('');

  // Fire-and-forget: record the search for the day-history review. Never block
  // or break the search if logging fails.
  function logSearch(kind: 'web' | 'youtube', searchEngine: string, query: string) {
    if (!active) return;
    void api.dayHistory
      .recordSearch(active.id, { date: isoDate(), kind, engine: searchEngine, query })
      .catch(() => {});
  }

  const prefs = useQuery({
    queryKey: ['preferences'],
    queryFn: () => api.preferences.get(),
  });

  const setEngine = useMutation({
    mutationFn: (engine: SearchEngineDto) => api.preferences.update({ searchEngine: engine }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['preferences'] }),
  });

  const engine: SearchEngineDto = prefs.data?.searchEngine ?? 'kiddle';
  const engineMeta = ENGINES.find((e) => e.key === engine) ?? ENGINES[0]!;

  function submitWeb(e: FormEvent) {
    e.preventDefault();
    const q = webQuery.trim();
    if (!q) return;
    logSearch('web', engine, q);
    window.open(webUrlFor(engine, q), '_blank', 'noopener');
  }

  function submitYouTube(e: FormEvent) {
    e.preventDefault();
    const q = ytQuery.trim();
    if (!q) return;
    logSearch('youtube', 'youtube', q);
    window.open(youtubeUrlFor(q), '_blank', 'noopener');
  }

  return (
    <section className={styles.search} data-testid="search-page">
      <div className={styles.card}>
        <header className={styles.head}>
          <div className={styles.titleWrap}>
            <span className={styles.titleGlyph} aria-hidden="true">
              🔍
            </span>
            <h1 className={styles.title}>Search</h1>
          </div>
          <fieldset className={styles.engineRow}>
            <legend
              className="sr-only"
              style={{
                position: 'absolute',
                width: 1,
                height: 1,
                padding: 0,
                margin: -1,
                overflow: 'hidden',
                clip: 'rect(0, 0, 0, 0)',
                whiteSpace: 'nowrap',
                border: 0,
              }}
            >
              Engine
            </legend>
            {ENGINES.map((e) => (
              <button
                key={e.key}
                type="button"
                role="radio"
                aria-checked={engine === e.key}
                onClick={() => setEngine.mutate(e.key)}
                className={styles.engineButton}
                data-testid={`engine-${e.key}`}
              >
                <span
                  className={`${styles.engineDot} ${styles[e.dotClass] ?? ''}`}
                  aria-hidden="true"
                />
                {e.label}
              </button>
            ))}
          </fieldset>
        </header>

        <hr className={styles.divider} />

        <form onSubmit={submitWeb} className={styles.form}>
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="search-web-input">
              <span className={styles.fieldGlyph} aria-hidden="true">
                🌐
              </span>
              Web
            </label>
            <input
              id="search-web-input"
              type="search"
              className={styles.input}
              value={webQuery}
              onChange={(ev) => setWebQuery(ev.target.value)}
              placeholder={engineMeta.placeholder}
              data-testid="search-web-input"
            />
          </div>
          <button type="submit" className={styles.submit} data-testid="search-web-submit">
            Search
          </button>
        </form>

        <form onSubmit={submitYouTube} className={styles.form}>
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="search-youtube-input">
              <span className={styles.fieldGlyph} aria-hidden="true">
                ▶️
              </span>
              YouTube
            </label>
            <input
              id="search-youtube-input"
              type="search"
              className={styles.input}
              value={ytQuery}
              onChange={(ev) => setYtQuery(ev.target.value)}
              placeholder="Search YouTube…"
              data-testid="search-youtube-input"
            />
          </div>
          <button type="submit" className={styles.submit} data-testid="search-youtube-submit">
            Search
          </button>
        </form>
      </div>
    </section>
  );
}
